import React from 'react';
import styled from 'styled-components';
import { User } from '../../types';

const UserListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const Header = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e1e5e9;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 6px;
  color: #666;
  transition: background-color 0.2s ease;
  display: none;

  &:hover {
    background: #f0f0f0;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const UserCount = styled.span`
  font-size: 0.8rem;
  color: #666;
`;

const UsersContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const UserItem = styled.div<{ isCurrentUser: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background: ${props => props.isCurrentUser ? '#f8f9ff' : 'transparent'};
  border-left: ${props => props.isCurrentUser ? '3px solid #667eea' : '3px solid transparent'};

  &:hover {
    background: #f8f9fa;
  }
`;

const UserAvatar = styled.div<{ isOnline: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  position: relative;
  flex-shrink: 0;
  opacity: ${props => props.isOnline ? 1 : 0.6};

  &::after {
    content: '';
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => props.isOnline ? '#48bb78' : '#e53e3e'};
    border: 2px solid white;
  }
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const Username = styled.div<{ isCurrentUser: boolean }>`
  font-weight: ${props => props.isCurrentUser ? 600 : 500};
  color: ${props => props.isCurrentUser ? '#667eea' : '#333'};
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserStatus = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-top: 0.125rem;
`;

const OnlineIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #48bb78;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: #666;
  text-align: center;

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
    opacity: 0.3;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: none;
  border-bottom: 1px solid #e1e5e9;
  font-size: 0.9rem;
  outline: none;
  background: #f8f9fa;

  &::placeholder {
    color: #999;
  }

  &:focus {
    background: white;
    border-bottom-color: #667eea;
  }
`;

interface UserListProps {
  users: User[];
  currentUser: User;
  onClose: () => void;
}

const UserList: React.FC<UserListProps> = ({
  users,
  currentUser,
  onClose
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredUsers = React.useMemo(() => {
    if (!searchTerm) return users;
    
    return users.filter(user =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  const sortedUsers = React.useMemo(() => {
    return filteredUsers.sort((a, b) => {
      // Current user first
      if (a.id === currentUser.id) return -1;
      if (b.id === currentUser.id) return 1;
      
      // Then sort by username
      return a.username.localeCompare(b.username);
    });
  }, [filteredUsers, currentUser]);

  const getUserInitials = (username: string): string => {
    return username
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusText = (user: User): string => {
    if (user.id === currentUser.id) return 'You';
    return 'Online';
  };

  return (
    <UserListContainer>
      <Header>
        <div>
          <Title>Users</Title>
          <UserCount>
            {users.length} {users.length === 1 ? 'user' : 'users'} online
          </UserCount>
        </div>
        <CloseButton onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
          </svg>
        </CloseButton>
      </Header>

      <SearchInput
        type="text"
        placeholder="Search users..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <UsersContainer>
        {sortedUsers.length === 0 ? (
          searchTerm ? (
            <EmptyState>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
              </svg>
              <p>No users found for "{searchTerm}"</p>
            </EmptyState>
          ) : (
            <EmptyState>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M16,4C18.21,4 20,5.79 20,8C20,10.21 18.21,12 16,12C13.79,12 12,10.21 12,8C12,5.79 13.79,4 16,4M16,14C20.42,14 24,15.79 24,18V20H8V18C8,15.79 11.58,14 16,14Z"/>
              </svg>
              <p>No users online</p>
            </EmptyState>
          )
        ) : (
          sortedUsers.map((user) => (
            <UserItem
              key={user.id}
              isCurrentUser={user.id === currentUser.id}
            >
              <UserAvatar isOnline={true}>
                {getUserInitials(user.username)}
              </UserAvatar>
              
              <UserInfo>
                <Username isCurrentUser={user.id === currentUser.id}>
                  {user.username}
                </Username>
                <UserStatus>
                  {getStatusText(user)}
                </UserStatus>
              </UserInfo>
              
              <OnlineIndicator />
            </UserItem>
          ))
        )}
      </UsersContainer>
    </UserListContainer>
  );
};

export default UserList; 